/**
 * Cart Permalink Handler
 *
 * This script allows you to link directly to the cart with items pre-added and
 * discount codes applied SERVER-SIDE by Shopify, WITHOUT redirecting to checkout.
 */

class CartPermalinkHandler {
  constructor() {
    this.init();
  }

  init() {
    console.log('[Cart Permalink] Initializing...');
    console.log('[Cart Permalink] Current URL:', window.location.href);
    console.log('[Cart Permalink] Pathname:', window.location.pathname);

    // Only process if we're on the cart page
    if (window.location.pathname !== '/cart') {
      console.log('[Cart Permalink] Not on cart page, skipping');
      return;
    }

    // Check both current URL params and sessionStorage (in case Shopify redirected)
    const params = new URLSearchParams(window.location.search);
    let addParam = params.get('add');
    let discountParam = params.get('discount') || params.get('code');

    // Check for ?add=variantId format (must check this FIRST, before checking for redirect)
    if (addParam) {
      const variantId = addParam;
      const discount = discountParam;

      console.log('[Cart Permalink] Processing:', { variantId, discount });

      if (variantId) {
        this.handleCartAdd(variantId, discount);
        return; // Important: return here to prevent checking after redirect
      }
    }

    // No add param and no expected discount - normal cart page load, do nothing
    console.log('[Cart Permalink] Normal cart page load, no action needed');
  }

  async handleCartAdd(variantId, discountCode) {
    console.log('[Cart Permalink] handleCartAdd called:', { variantId, discountCode });
    // Cart tokens captured at each step so we can detect cookie-jar divergence
    // (the WebView/Safari theory: /cart/add.js writes to one cart, the next
    // /cart.js reads from another).
    const tokens = { initial: null, postAdd: null, final: null };
    let addStatus = null;
    this.reportAnomaly('handlerStart', { variantId, discountCode });
    try {
      // Show loading state
      this.showLoading();

      // --- helpers --------------------------------------------------------------

      const getCart = () => fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

      // Extract currently *applied* discount codes. Shopify keeps entries in
      // cart.discount_codes forever with `applicable: false` once they've
      // been tried — e.g. a code from a previous proposal that no longer
      // matches any line item. Including those in the merged comma-string
      // has caused Shopify to reject the new code on at least one report,
      // so we filter to applicable=true only.
      const extractAppliedCodes = (cart) => {
        const fromLegacy = Array.isArray(cart.discount_codes)
          ? cart.discount_codes
            .filter(d => d && d.applicable !== false)
            .map(d => (d && (d.code || d.title)) || '')
            .filter(Boolean)
          : [];
        const fromCartLevel = Array.isArray(cart.cart_level_discount_applications)
          ? cart.cart_level_discount_applications
            .map(d => (d && (d.title || d.code)) || '')
            .filter(Boolean)
          : [];
        // Normalize, lowercase for uniqueness, preserve original casing of first seen
        const unique = {};
        [...fromLegacy, ...fromCartLevel].forEach(code => {
          const key = String(code).trim().toLowerCase();
          if (key && !unique[key]) unique[key] = code.trim();
        });
        return Object.values(unique);
      };

      // Modern Shopify cart responses populate multiple discount fields:
      //  - line.discounts (legacy, array)
      //  - line.line_level_discount_allocations (modern, array)
      //  - line.line_level_total_discount (modern, integer in cents)
      //  - line.total_discount (legacy, integer)
      // We check all of them so we don't false-negative if Shopify changes
      // which field it populates.
      const lineHasDiscount = (line) => {
        if (!line) return false;
        if ((line.discounts?.length || 0) > 0) return true;
        if ((line.line_level_discount_allocations?.length || 0) > 0) return true;
        if ((line.line_level_total_discount || 0) > 0) return true;
        if ((line.total_discount || 0) > 0) return true;
        return false;
      };

      const applyCodes = async (codesArray) => {
        const merged = codesArray
          .map(c => String(c || '').trim())
          .filter(Boolean);

        // Shopify expects comma-separated string for multiple codes
        const payload = { discount: merged.join(',') };

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        return res.ok;
      };

      // --- 1) Read cart ---------------------------------------------------------
      let cart = await getCart();
      tokens.initial = cart.token || null;

      // --- 2) Add the item ONLY if not present ----------------------------------
      const hasVariant = cart.items?.some(it => String(it.variant_id) === String(variantId));
      console.log('[Cart Permalink] Has variant:', hasVariant);
      if (!hasVariant) {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 , properties: { _maiAdded: 'true' }}]
            // Important: DO NOT pass `discount` here—doing so can replace existing codes.
          })
        });

        console.log('[Cart Permalink] Add response:', addRes);
        addStatus = addRes.status;

        if (!addRes.ok) {
          const raw = await addRes.text();
          let parsed = {};
          try { parsed = JSON.parse(raw); } catch { /* ignored */ }

          const status = addRes.status || parsed.status;
          const msg = String(parsed.message || '').toLowerCase();
          const desc = String(parsed.description || '').toLowerCase();
          const rawLower = raw.toLowerCase();

          // English + Swedish signals
          const soldOutSignals = [
            'sold out', 'out of stock', 'unavailable', 'not available',
            'kan inte hitta variant', // “cannot find variant”
            'slut i lager',           // “out of stock”
            'ej tillgänglig', 'otillgänglig'
          ];

          const isSoldOut = soldOutSignals.some(s =>
            msg.includes(s) || desc.includes(s) || rawLower.includes(s)
          );

          console.warn('Add to cart failed:', { status, parsed, raw });
          this.reportAnomaly('addFailed', {
            variantId,
            discountCode,
            status,
            parsed,
            raw: raw && raw.slice(0, 2000),
            tokens,
          });

          if (status === 422 || isSoldOut) {
            this.showSoldOutDialog();
            this.hideLoading();
            return; // stop further discount logic
          }

          this.hideLoading();
          throw new Error(`Failed to add item: ${raw}`);
        }

        cart = await getCart();
        tokens.postAdd = cart.token || null;
      }


      // --- 3) Preserve existing codes and add the new one -----------------------
      const existingCodes = extractAppliedCodes(cart);
      const nextCodesMap = {};
      existingCodes.forEach(c => { nextCodesMap[c.toLowerCase()] = c; });
      if (discountCode) nextCodesMap[discountCode.toLowerCase()] = discountCode;
      console.log('[Cart Permalink] Next codes map:', nextCodesMap);

      const nextCodes = Object.values(nextCodesMap);

      // Apply merged codes via /cart/update.js (idempotent if already applied)
      await applyCodes(nextCodes);

      // --- 4) Re-fetch and verify line got discounted by THIS code --------------
      const fresh = await getCart();
      tokens.final = fresh.token || null;
      const line = fresh.items?.find(it => String(it.variant_id) === String(variantId));
      console.log('[Cart Permalink] Line:', line);

      const noDiscountOnLine = !lineHasDiscount(line);

      if (noDiscountOnLine) {
        this.reportAnomaly('noDiscountOnLine', {
          variantId,
          discountCode,
          cartHadVariant: hasVariant,
          addStatus,
          tokens,
          cart: fresh,
        });
        const params = new URLSearchParams(window.location.search);
        if (params.get('x')) {
          // New links created after Nov 5 2025
          // Show your dialog that the code isn’t eligible/expired for this item and remove the item from the cart
          this.showDiscountZeroDialog(
            variantId,
            line?.image || null,
            line?.product_title || ''
          );
          this.hideLoading();
          return;
        }
        // Old links created before Nov 5 2025
        // Show your dialog that the code isn’t eligible/expired for this item and remove the item from the cart
        this.showDiscountExpiredDialog(
          variantId,
          line?.url || null,
          line?.image || null,
          line?.product_title || ''
        );
        this.hideLoading();
        await this.removeItemFromCart({
          variantId,
          cartSnapshot: fresh,     // pass the cart you already fetched
          lineItemKey: line?.key   // best signal if you have it
        });
        return;
      }

      this.reportAnomaly('handlerSuccess', {
        variantId,
        discountCode,
        cartHadVariant: hasVariant,
        addStatus,
        tokens,
      });

      // Remove query params and reload once to show the new cart
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      window.location.reload();
    } catch (error) {
      console.error('[Cart Permalink] Error adding item to cart:', error);
      this.reportAnomaly('exception', {
        variantId,
        discountCode,
        message: error && error.message,
        stack: error && error.stack,
        addStatus,
        tokens,
      });
      this.hideLoading();
      //window.location.href = '/cart';
    }
  }

  // Fire-and-forget diagnostic POST to Firebase Functions. Logs land in GCP
  // Cloud Logging under `cartPermalinkDebug` so we can diff a failing user's
  // cart snapshot against a working one. `keepalive` survives page unload.
  reportAnomaly(kind, data) {
    try {
      const base = window.API_BASE_URL;
      if (!base) return;
      const payload = {
        kind,
        ts: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...data,
      };
      fetch(base + '/cartPermalinkDebug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit',
        mode: 'cors',
      }).catch(() => {});
    } catch (_) { /* telemetry must never throw */ }
  }


  async removeItemFromCart({ variantId, cartSnapshot = null }) {
    try {
      // Use existing cart if provided; otherwise fetch
      const cart = cartSnapshot || await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

      // Find the line we added (has our custom _maiAdded flag)
      const line = cart.items?.find(
        it => String(it.variant_id) === String(variantId) && it.properties?._maiAdded === 'true'
      );
      if (!line) return; // user-added item or not found → do nothing

      // Remove our tagged line
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: line.key, quantity: 0 })
      });
      console.log('[Cart] Removed Mai-added item:', line);

      if (!res.ok) {
        const text = await res.text();
        console.error('[Cart] Remove failed:', res.status, text);
        throw new Error(`Failed to remove item: ${res.status}`);
      }

      return await res.json(); // updated cart
    } catch (err) {
      console.error('[Cart] Error removing item:', err);
      throw err;
    }
  }


  async getProductUrlFromVariant(variantId) {
    console.log('[Cart Permalink] getProductUrlFromVariant called for variant:', variantId);
    try {
      // Fetch variant data to get product information
      const response = await fetch(`/variants/${variantId}.js`);
      if (!response.ok) {
        console.error('[Cart Permalink] Variant fetch failed:', response.status, response.statusText);
        throw new Error(`Failed to fetch variant data: ${response.status}`);
      }
      const variantData = await response.json();
      console.log('[Cart Permalink] Variant data received:', variantData);

      // The variant data should include product handle or we can construct URL
      // Format: [/<locale>]/products/{handle}?variant={variantId}
      if (variantData.product && variantData.product.handle) {
        // Derive locale prefix from current path so non-default locales link correctly
        const localeMatch = window.location.pathname.match(/^(\/[a-z]{2}(?:-[a-z]{2})?)\//i);
        const localePrefix = localeMatch ? localeMatch[1] : '';
        const url = `${localePrefix}/products/${variantData.product.handle}?variant=${variantId}`;
        console.log('[Cart Permalink] Constructed URL from product handle:', url);
        return url;
      }

      // Fallback: try to get from variant URL if available
      if (variantData.url) {
        console.log('[Cart Permalink] Using variant URL:', variantData.url);
        return variantData.url;
      }

      // Last resort: return empty string (dialog will still show)
      console.log('[Cart Permalink] No URL found in variant data');
      return '';
    } catch (error) {
      console.error('[Cart Permalink] Error fetching variant data:', error);
      return '';
    }
  }

  showSoldOutDialog() {
    console.log('[Cart Permalink] showSoldOutDialog called');

    // Create dialog overlay
    const overlay = document.createElement('div');
    overlay.id = 'cart-permalink-sold-out-dialog';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: 'DM Sans', sans-serif;
    `;

    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: white;
      padding: 5rem 2rem;
      border-radius: 20px;
      max-width: 500px;
      width: 90%;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    dialog.innerHTML = `
      <h2 style="font-family: 'DM Sans', sans-serif; font-size: 24px; font-weight: 600; color: #222222; line-height: 1.2; margin: 0 0 16px 0;">
        Den är tyvärr såld
      </h2>
      <p style="font-family: 'DM Sans', sans-serif; font-size: 16px; color: #8C8C8C; line-height: 1.3; margin: 0 0 32px 0;">
        Produkten du är ute efter har köpts av någon annan. Hoppas vi kan hjälpa dig hitta något annat framöver!
      </p>
      <a href="/"
         class="button button--primary"
         style="display: flex;
                justify-content: center;
                align-items: center;
                padding: 0.75rem 1.5rem;
                text-decoration: none;
                font-family: 'DM Sans', sans-serif;
                font-weight: 400;
                min-width: 200px;
                transition: all 0.2s ease;">
        Utforska Mai
      </a>
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Close dialog when clicking the primary link (prevent navigation)
    const primaryLink = dialog.querySelector('a.button');
    if (primaryLink) {
      primaryLink.addEventListener('click', (e) => {
        e.preventDefault();
        overlay.remove();
        window.history.replaceState({}, '', '/cart');
      });
    }

    // Close dialog when clicking overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
        window.history.replaceState({}, '', '/cart');
      }
    });

    // Clean up URL parameters
    window.history.replaceState({}, '', '/cart');
  }

  async showDiscountExpiredDialog(variantId, productUrl = null, productImage = null, productImageAlt = '') {
    console.log('[Cart Permalink] showDiscountExpiredDialog called for variant:', variantId);
    console.log('[Cart Permalink] Product URL:', productUrl);
    console.log('[Cart Permalink] Product Image:', productImage);

    // Get product URL if not provided
    if (!productUrl) {
      productUrl = await this.getProductUrlFromVariant(variantId);
    }

    // Create dialog overlay
    const overlay = document.createElement('div');
    overlay.id = 'cart-permalink-dialog';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: 'DM Sans', sans-serif;
    `;

    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: white;
      padding: 5rem 2rem;
      border-radius: 20px;
      max-width: 500px;
      width: 90%;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    dialog.innerHTML = `
      <div style="height: 200px; margin-bottom: 1.5rem; display: flex; align-items: center; justify-content: center;">
        ${productImage ? `
          <img src="${productImage}"
               alt="${productImageAlt}"
               style="height: 200px;
                      max-height: 200px;
                      width: auto;
                      border-radius: 8px;
                      object-fit: cover;">
        ` : ''}
      </div>
      <h2 style="font-family: 'DM Sans', sans-serif; font-size: 24px; font-weight: 600; color: #222222; line-height: 1.2; margin: 20px 0 10px 0;">
        Rabattlänken har gått ut
      </h2>
      <p style="font-family: 'DM Sans', sans-serif; font-size: 16px; color: #8C8C8C; line-height: 1.3; margin: 0 0 32px 0;">
        Rabattkoden är inte längre giltig, men priset kan ha sänkts på varan. Besök produkten för att se aktuellt pris.
      </p>
      ${productUrl ? `
        <a href="${productUrl}"
           class="button button--primary"
           style="display: flex;
                  justify-content: center;
                  align-items: center;
                  padding: 0.75rem 1.5rem;
                  text-decoration: none;
                  font-family: 'DM Sans', sans-serif;
                  font-weight: 400;
                  min-width: 200px;
                  transition: all 0.2s ease;">
          Gå till produkten
        </a>
      ` : ''}
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Close dialog when clicking overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
        // Clean up URL parameters
        window.history.replaceState({}, '', '/cart');
      }
    });

    // Clean up URL parameters when dialog is shown
    window.history.replaceState({}, '', '/cart');
  }

  async showDiscountZeroDialog(variantId, productImage = null, productImageAlt = '') {
    console.log('[Cart Permalink] showDiscountZeroDialog called for variant:', variantId);
    console.log('[Cart Permalink] Product Image:', productImage);

    // Create dialog overlay
    const overlay = document.createElement('div');
    overlay.id = 'cart-permalink-dialog';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: 'DM Sans', sans-serif;
    `;

    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: white;
      padding: 5rem 2rem;
      border-radius: 20px;
      max-width: 500px;
      width: 90%;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    dialog.innerHTML = `
      <div style="height: 200px; margin-bottom: 1.5rem; display: flex; align-items: center; justify-content: center;">
        ${productImage ? `
          <img src="${productImage}"
               alt="${productImageAlt}"
               style="height: 200px;
                      max-height: 200px;
                      width: auto;
                      border-radius: 8px;
                      object-fit: cover;">
        ` : ''}
      </div>
      <h2 style="font-family: 'DM Sans', sans-serif; font-size: 24px; font-weight: 600; color: #222222; line-height: 1.2; margin: 20px 0 10px 0;">
        Tillagd i varukorgen
      </h2>
      <p style="font-family: 'DM Sans', sans-serif; font-size: 16px; color: #8C8C8C; line-height: 1.3; margin: 0 0 32px 0;">
        Varans pris har sänkts till under ditt prisförslag och har lagts till i varukorgen.
      </p>
      <button href="#"
        type="button"
        class="button button--primary"
        style="display: flex;
              justify-content: center;
              align-items: center;
              padding: 0.75rem 1.5rem;
              text-decoration: none;
              font-family: 'DM Sans', sans-serif;
              font-weight: 400;
              min-width: 200px;
              transition: all 0.2s ease;">
        Ok
      </button>
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Close dialog when clicking the primary link (prevent navigation)
    const primaryLink = dialog.querySelector('button.button');
    if (primaryLink) {
      primaryLink.addEventListener('click', (e) => {
        e.preventDefault();
        overlay.remove();
        window.history.replaceState({}, '', '/cart');
        window.location.reload();
      });
    }

    // Close dialog when clicking overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
        // Clean up URL parameters
        window.history.replaceState({}, '', '/cart');
      }
    });

    // Clean up URL parameters when dialog is shown
    window.history.replaceState({}, '', '/cart');
  }


  showLoading() {
    // Create a simple loading overlay
    const overlay = document.createElement('div');
    overlay.id = 'cart-permalink-loading';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.95);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      font-size: 16px;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div class="loading-overlay__spinner">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width: 30px; height: 30px;">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30" style="stroke: #000;"></circle>
          </svg>
        </div>
        <div style="margin-bottom: 10px; color: #000;">Laddar rabatt</div>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  hideLoading() {
    const overlay = document.getElementById('cart-permalink-loading');
    if (overlay) {
      overlay.remove();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartPermalinkHandler();
  });
} else {
  new CartPermalinkHandler();
}


